/**
 * Venue search for the event form: Mapbox Search Box (suggest → retrieve) with
 * Geocoding v6 as a fallback when the token is refused. Parsers are pure so they
 * can be tested with recorded responses.
 */
const SEARCH_BOX = 'https://api.mapbox.com/search/searchbox/v1'
const GEOCODE = 'https://api.mapbox.com/search/geocode/v6/forward'

export interface VenuePlace {
  /** Venue name, or the street line for a plain address. */
  location: string
  /** Street line when it adds something to `location`, else ''. */
  address: string
  city: string
  latitude: number
  longitude: number
}

export interface VenueSuggestion {
  id: string
  name: string
  /** Grey second line: full address without the country. */
  description: string
  kind: 'poi' | 'address' | 'place'
  /** Already resolved (Geocoding fallback): no retrieve call needed. */
  place?: VenuePlace
}

/** The part of a Mapbox feature / suggestion that we read. */
interface MapboxProps {
  mapbox_id?: string
  name?: string
  feature_type?: string
  address?: string
  full_address?: string
  place_formatted?: string
  context?: {
    place?: { name?: string }
    locality?: { name?: string }
    country?: { name?: string }
  }
  coordinates?: { latitude?: number; longitude?: number }
}
interface SuggestResponse {
  suggestions?: MapboxProps[]
}
interface FeaturesResponse {
  features?: { properties?: MapboxProps }[]
}

const stripCountry = (fullAddress: string | undefined, country: string | undefined) => {
  const text = fullAddress ?? ''
  if (!country) return text
  const suffix = `, ${country}`
  return text.endsWith(suffix) ? text.slice(0, -suffix.length) : text
}

const kindOf = (featureType: string | undefined): VenueSuggestion['kind'] =>
  featureType === 'poi' ? 'poi' : featureType === 'place' ? 'place' : 'address'

export function parseSuggestions(response: SuggestResponse): VenueSuggestion[] {
  const list = Array.isArray(response?.suggestions) ? response.suggestions : []
  return list.flatMap((s) =>
    typeof s?.mapbox_id === 'string' && typeof s.name === 'string'
      ? [
          {
            id: s.mapbox_id,
            name: s.name,
            description: stripCountry(
              s.full_address ?? s.place_formatted,
              s.context?.country?.name,
            ),
            kind: kindOf(s.feature_type),
          },
        ]
      : [],
  )
}

const sameText = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase()

function placeFrom(
  name: string,
  props: MapboxProps,
  latitude: number,
  longitude: number,
): VenuePlace {
  const street = typeof props.address === 'string' ? props.address : ''
  return {
    location: name,
    address: street && !sameText(street, name) ? street : '',
    city: props.context?.place?.name ?? props.context?.locality?.name ?? '',
    latitude,
    longitude,
  }
}

/** `/retrieve/{id}` response → place. The suggestion name wins (cleaner casing). */
export function parseRetrieve(
  response: FeaturesResponse,
  suggestionName: string,
): VenuePlace | null {
  const props = response?.features?.[0]?.properties
  const latitude = props?.coordinates?.latitude
  const longitude = props?.coordinates?.longitude
  if (!props || typeof latitude !== 'number' || typeof longitude !== 'number') return null
  return placeFrom(suggestionName || props.name || '', props, latitude, longitude)
}

/** Geocoding v6 `forward` response → suggestions that already carry their place. */
export function parseGeocode(response: FeaturesResponse): VenueSuggestion[] {
  const features = Array.isArray(response?.features) ? response.features : []
  return features.flatMap((feature) => {
    const props = feature.properties
    const latitude = props?.coordinates?.latitude
    const longitude = props?.coordinates?.longitude
    if (!props?.name || typeof latitude !== 'number' || typeof longitude !== 'number') return []
    return [
      {
        id: props.mapbox_id ?? `${latitude},${longitude}`,
        name: props.name,
        description: stripCountry(
          props.full_address ?? props.place_formatted,
          props.context?.country?.name,
        ),
        kind: kindOf(props.feature_type),
        place: placeFrom(props.name, props, latitude, longitude),
      },
    ]
  })
}

/** "5 Parv. Alan Turing · Paris": one line for the venue, whatever was filled. */
export const venueSummary = (venue: { location: string; address: string; city: string }) =>
  [venue.location, venue.address, venue.city].filter((part) => part.trim()).join(', ')

/** Bias results toward the organizer's market (`ip` when unknown). */
export const searchProximity = (language: string) =>
  language === 'fr' ? '2.35,48.85' : language === 'es' ? '-3.70,40.42' : 'ip'

export const newSearchSession = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`

export interface VenueSearchOptions {
  token: string
  language: string
  session: string
  signal?: AbortSignal
}

async function getJson<T>(url: string, signal?: AbortSignal): Promise<{ status: number; body: T }> {
  const response = await fetch(url, { signal })
  const body = (response.ok ? await response.json() : {}) as T
  return { status: response.status, body }
}

/** Search Box `suggest`, falling back to Geocoding v6 if the token is refused. */
export async function fetchVenueSuggestions(
  query: string,
  { token, language, session, signal }: VenueSearchOptions,
): Promise<VenueSuggestion[]> {
  // Built from plain objects: React Native's URLSearchParams has no delete()/set().
  const common = {
    q: query,
    language,
    limit: '5',
    proximity: searchProximity(language),
    access_token: token,
  }
  const params = new URLSearchParams({
    ...common,
    types: 'poi,address,place',
    session_token: session,
  })
  const suggest = await getJson<SuggestResponse>(
    `${SEARCH_BOX}/suggest?${params.toString()}`,
    signal,
  )
  if (suggest.status !== 401 && suggest.status !== 403) {
    if (suggest.status >= 400) throw new Error(`Mapbox suggest ${suggest.status}`)
    return parseSuggestions(suggest.body)
  }
  const geocodeParams = new URLSearchParams(common)
  const geocode = await getJson<FeaturesResponse>(
    `${GEOCODE}?${geocodeParams.toString()}`,
    signal,
  )
  if (geocode.status >= 400) throw new Error(`Mapbox geocode ${geocode.status}`)
  return parseGeocode(geocode.body)
}

export async function retrieveVenue(
  suggestion: VenueSuggestion,
  { token, language, session, signal }: VenueSearchOptions,
): Promise<VenuePlace | null> {
  if (suggestion.place) return suggestion.place
  const params = new URLSearchParams({ session_token: session, language, access_token: token })
  const { status, body } = await getJson<FeaturesResponse>(
    `${SEARCH_BOX}/retrieve/${encodeURIComponent(suggestion.id)}?${params.toString()}`,
    signal,
  )
  if (status >= 400) throw new Error(`Mapbox retrieve ${status}`)
  return parseRetrieve(body, suggestion.name)
}
